import { FC, MouseEvent, ReactNode, useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import { useNavigate } from "react-router-dom";
import PhCaretDownSvg from "../../../assets/svg-fc/PhCaretDownSvg";
import PhSidebarSimpleSvg from "../../../assets/svg-fc/PhSidebarSimpleSvg";
import PhXSvg from "../../../assets/svg-fc/PhXSvg";
import youCaterLogoCollapsed from "../../../assets/images/youCaterLogoCollapsed.png";
import palette from "../../../theme/palette";
import { UI_TOKENS as NAV_TOKENS } from "../../../constants/designTokens";

export interface iSideNavChild {
	key: string;
	label: string;
	href?: string;
	/** The list's `?type=` on an event detail page opened from it. */
	query?: string;
	dot?: boolean;
	/** Instead of navigating — e.g. an external page or an in-portal viewer. */
	onClick?: () => void;
}

export interface iSideNavItem {
	key: string;
	label: string;
	icon: ReactNode;
	/** Items without children navigate; groups expand. */
	href?: string;
	query?: string;
	dot?: boolean;
	dividerBefore?: boolean;
	children?: iSideNavChild[];
}

interface iSideNavProps {
	items: iSideNavItem[];
	/** The item or child key of the current page. */
	activeKey: string | null;
	/** Below the desktop width — the nav becomes a drawer opened from the header's menu button. */
	compact: boolean;
	drawerOpen: boolean;
	onCloseDrawer: () => void;
	logoSrc?: string;
	/** Maktab's interface size (CSS zoom) for the nav and its tooltip. */
	zoom?: number;
}

const NAV_KEY = "yc-partner-sidebar-collapsed";
/** A group's open animation (240ms) — its children are measured once they've landed. */
const GROUP_OPEN_MS = 260;
const EASE = "cubic-bezier(.23,1,.32,1)";
/** The active item: the theme's primary, on a 10% primary pill. */
const ACTIVE = palette.primary.main;
const SELECTED = `inset 0 0 0 100px ${palette.primary.main}1A`;

const readCollapsed = () => {
	try {
		return localStorage.getItem(NAV_KEY) === "1";
	} catch {
		return false;
	}
};

const dotSx = { width: 7, height: 7, flex: "none", borderRadius: "50%", bgcolor: NAV_TOKENS.sage600 };

const SideNav: FC<iSideNavProps> = ({ items, activeKey, compact, drawerOpen, onCloseDrawer, logoSrc, zoom = 1 }) => {
	const navigate = useNavigate();
	const [storedCollapsed, setStoredCollapsed] = useState(readCollapsed);
	const [open, setOpen] = useState<Record<string, boolean>>({});
	const [markHover, setMarkHover] = useState(false);
	const [tip, setTip] = useState<{ label: string; top: number } | null>(null);
	const collapsed = compact ? false : storedCollapsed;
	const labelOpacity = collapsed ? 0 : 1;

	// The group holding the current page starts open.
	const activeGroup = items.find((item) => item.children?.some((child) => child.key === activeKey))?.key;
	useEffect(() => {
		if (activeGroup) setOpen((prev) => (prev[activeGroup] ? prev : { ...prev, [activeGroup]: true }));
	}, [activeGroup]);

	const setCollapsed = (next: boolean) => {
		try {
			localStorage.setItem(NAV_KEY, next ? "1" : "0");
		} catch {
			// Private mode — the choice just isn't remembered.
		}
		setStoredCollapsed(next);
		setTip(null);
		setMarkHover(false);
	};

	const listRef = useRef<HTMLDivElement>(null);
	const groupRefs = useRef<Record<string, HTMLDivElement | null>>({});
	const revealTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
	useEffect(() => () => clearTimeout(revealTimer.current), []);

	/**
	 * Opening a group low in the list: once it has expanded, scroll the nav (only the nav) so its children are in
	 * view. Nothing moves if they already fit; a group taller than the list keeps its header.
	 */
	const revealGroup = (key: string) => {
		clearTimeout(revealTimer.current);
		revealTimer.current = setTimeout(() => {
			const list = listRef.current;
			const group = groupRefs.current[key];
			if (!list || !group) return;
			const top = group.offsetTop;
			const bottom = top + group.offsetHeight;
			const viewBottom = list.scrollTop + list.clientHeight;
			if (bottom <= viewBottom && top >= list.scrollTop) return;
			list.scrollTo({ top: Math.min(top, bottom - list.clientHeight), behavior: "smooth" });
		}, GROUP_OPEN_MS);
	};

	const go = (href: string) => {
		navigate(href);
		if (compact) onCloseDrawer();
	};

	const toggleButtonSx = {
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		width: 32,
		height: 32,
		flex: "none",
		border: "none",
		borderRadius: NAV_TOKENS.radiusMd,
		background: "transparent",
		color: NAV_TOKENS.textSecondary,
		cursor: "pointer",
		transition: `background-color 150ms ease, color 150ms ease, transform 120ms ${EASE}`,
		"&:hover": { bgcolor: NAV_TOKENS.surfaceHover, color: NAV_TOKENS.textStrong },
		"&:active": { transform: "scale(0.96)" },
		// No browser focus ring after a click.
		"&:focus, &:focus-visible": { outline: "none" },
	};

	return (
		<>
			{compact && drawerOpen && (
				<Box
					onClick={onCloseDrawer}
					sx={{
						position: "fixed",
						inset: 0,
						zIndex: 1300,
						bgcolor: NAV_TOKENS.overlay,
						animation: "ycNavFade 200ms ease",
						"@keyframes ycNavFade": { from: { opacity: 0 }, to: { opacity: 1 } },
					}}
				/>
			)}

			<Box
				component="nav"
				aria-label="Main navigation"
				data-collapsed={collapsed ? "true" : "false"}
				className="SideNavDrawer"
				onClick={(event: MouseEvent) => event.stopPropagation()}
				sx={{
					display: "flex",
					flexDirection: "column",
					height: "100%",
					flexShrink: 0,
					overflow: "hidden",
					bgcolor: NAV_TOKENS.surfaceCard,
					borderRight: `1px solid ${NAV_TOKENS.borderSubtle}`,
					fontFamily: NAV_TOKENS.fontSans,
					zoom,
					"& button:focus, & button:focus-visible, & a:focus, & a:focus-visible": { outline: "none" },
					"@keyframes ycNavFade": { from: { opacity: 0 }, to: { opacity: 1 } },
					...(compact
						? {
								position: "fixed",
								left: 0,
								top: 0,
								bottom: 0,
								zIndex: 1301,
								width: 260,
								transform: `translateX(${drawerOpen ? "0" : "-105%"})`,
								transition: `transform 240ms ${EASE}`,
								boxShadow: drawerOpen ? NAV_TOKENS.shadowLg : "none",
							}
						: { position: "relative", isolation: "isolate", width: collapsed ? 72 : 240, transition: `width 240ms ${EASE}` }),
				}}
			>
				{collapsed ? (
					<Box
						sx={{
							height: 64,
							flex: "none",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							animation: "ycNavFade 200ms ease",
						}}
					>
						<Box
							component="button"
							type="button"
							onClick={() => setCollapsed(false)}
							onMouseEnter={() => setMarkHover(true)}
							onMouseLeave={() => setMarkHover(false)}
							aria-label="Expand sidebar"
							title="Expand sidebar"
							aria-expanded={false}
							sx={{
								position: "relative",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								width: 44,
								height: 44,
								border: "none",
								borderRadius: NAV_TOKENS.radiusMd,
								background: "transparent",
								cursor: "pointer",
								transition: `background-color 150ms ease, transform 120ms ${EASE}`,
								"&:hover": { bgcolor: NAV_TOKENS.surfaceHover },
								"&:active": { transform: "scale(0.96)" },
								"&:focus, &:focus-visible": { outline: "none" },
							}}
						>
							{/* The collapsed mark turns into the expand icon on hover. */}
							<Box
								component="img"
								src={youCaterLogoCollapsed}
								alt="YouCater"
								sx={{
									height: 26,
									width: "auto",
									display: "block",
									opacity: markHover ? 0 : 1,
									transition: "opacity 150ms ease",
								}}
							/>
							<Box
								sx={{
									position: "absolute",
									display: "flex",
									color: NAV_TOKENS.textStrong,
									opacity: markHover ? 1 : 0,
									transition: "opacity 150ms ease",
								}}
							>
								<PhSidebarSimpleSvg size={18} />
							</Box>
						</Box>
					</Box>
				) : (
					<Box
						sx={{
							height: 64,
							flex: "none",
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							gap: "10px",
							padding: "0 14px 0 24px",
							animation: "ycNavFade 200ms ease",
						}}
					>
						<Box
							component="img"
							src={logoSrc}
							alt="YouCater"
							sx={{ height: 24, width: "auto", display: "block" }}
						/>
						<Box
							component="button"
							type="button"
							onClick={compact ? onCloseDrawer : () => setCollapsed(true)}
							aria-label={compact ? "Close menu" : "Collapse sidebar"}
							title={compact ? "Close menu" : "Collapse sidebar"}
							aria-expanded
							sx={toggleButtonSx}
						>
							{compact ? <PhXSvg size={18} /> : <PhSidebarSimpleSvg size={18} />}
						</Box>
					</Box>
				)}

				<Box
					ref={listRef}
					sx={{
						flex: 1,
						minHeight: 0,
						overflowY: "auto",
						overflowX: "hidden",
						overscrollBehavior: "contain",
						scrollbarWidth: "thin",
						scrollbarColor: "transparent transparent",
						"&:hover": { scrollbarColor: "rgba(34,3,31,0.14) transparent" },
					}}
				>
					<Box
						sx={{
							position: "relative",
							display: "flex",
							flexDirection: "column",
							gap: "2px",
							padding: "4px 12px 12px",
						}}
					>
						{items.map((item) => {
							const hasChildren = Boolean(item.children?.length);
							const isOpen = hasChildren && Boolean(open[item.key]);
							const childVisible = isOpen && !collapsed;
							const containsActive =
								hasChildren && Boolean(item.children?.some((child) => child.key === activeKey));
							const selfActive = item.key === activeKey;
							const pillHere = selfActive || (containsActive && !childVisible);
							// A group carries its children's unread dot too — beside the caret, or on the icon when collapsed.
							const hasDot = Boolean(item.dot) || Boolean(item.children?.some((child) => child.dot));
							const cornerDot = collapsed && hasDot;
							const color = selfActive || containsActive ? ACTIVE : NAV_TOKENS.textStrong;

							return (
								<Box
									key={item.key}
									ref={(node: HTMLDivElement | null) => {
										groupRefs.current[item.key] = node;
									}}
									sx={{ display: "flex", flexDirection: "column" }}
								>
									{item.dividerBefore && (
										<Box sx={{ height: "1px", bgcolor: NAV_TOKENS.divider, margin: "8px 12px" }} />
									)}
									<Box
										component="button"
										type="button"
										onClick={() => {
											if (hasChildren) {
												if (collapsed) {
													setCollapsed(false);
													setOpen((prev) => ({ ...prev, [item.key]: true }));
													revealGroup(item.key);
												} else {
													setOpen((prev) => ({ ...prev, [item.key]: !isOpen }));
													if (!isOpen) revealGroup(item.key);
												}
												return;
											}
											if (item.href) go(item.href);
										}}
										onMouseEnter={(event: MouseEvent<HTMLElement>) => {
											if (!collapsed) return;
											// The rect is on screen; the zoomed tooltip's `top` is in zoomed px.
											const rect = event.currentTarget.getBoundingClientRect();
											setTip({ label: item.label, top: Math.round((rect.top + rect.height / 2) / zoom) });
										}}
										onMouseLeave={() => setTip(null)}
										aria-expanded={hasChildren ? childVisible : undefined}
										aria-current={selfActive ? "page" : undefined}
										aria-label={item.label}
										sx={{
											position: "relative",
											zIndex: 1,
											display: "flex",
											alignItems: "center",
											gap: "12px",
											width: "100%",
											height: 40,
											padding: "0 12px 0 15px",
											border: "none",
											borderRadius: NAV_TOKENS.radiusMd,
											background: "transparent",
											cursor: "pointer",
											fontFamily: NAV_TOKENS.fontSans,
											fontSize: 14,
											fontWeight: 500,
											color,
											boxShadow: pillHere ? SELECTED : "none",
											whiteSpace: "nowrap",
											overflow: "hidden",
											textAlign: "left",
											transition: "color 150ms ease, background-color 150ms ease, box-shadow 200ms ease",
											"&:hover": { bgcolor: "rgba(34,3,31,0.04)" },
										}}
									>
										<Box
											component="span"
											sx={{
												position: "relative",
												display: "inline-flex",
												alignItems: "center",
												justifyContent: "center",
												width: 18,
												height: 18,
												flex: "none",
												"& svg": { width: 18, height: 18 },
											}}
										>
											{item.icon}
											{cornerDot && (
												<Box
													component="span"
													sx={{
														...dotSx,
														position: "absolute",
														top: "-2px",
														right: "-3px",
														border: `1.5px solid ${NAV_TOKENS.surfaceCard}`,
														animation: "ycNavFade 200ms ease",
													}}
												/>
											)}
										</Box>
										<Box
											component="span"
											sx={{
												flex: 1,
												minWidth: 0,
												overflow: "hidden",
												textOverflow: "ellipsis",
												opacity: labelOpacity,
												transition: `opacity 120ms ${EASE}`,
											}}
										>
											{item.label}
										</Box>
										{hasDot && !collapsed && (
											<Box
												component="span"
												aria-hidden
												sx={{ ...dotSx, opacity: labelOpacity, transition: "opacity 120ms ease" }}
											/>
										)}
										{hasChildren && (
											<Box
												component="span"
												aria-hidden
												sx={{
													display: "inline-flex",
													flex: "none",
													opacity: labelOpacity,
													transform: `rotate(${isOpen ? 180 : 0}deg)`,
													transition: `transform 240ms ${EASE}, opacity 120ms ease`,
												}}
											>
												<PhCaretDownSvg size={14} />
											</Box>
										)}
									</Box>

									{hasChildren && (
										<Box
											sx={{
												display: "grid",
												gridTemplateRows: childVisible ? "1fr" : "0fr",
												opacity: childVisible ? 1 : 0,
												transition: `grid-template-rows 240ms ${EASE}, opacity 160ms ${EASE}`,
											}}
										>
											<Box sx={{ overflow: "hidden", minHeight: 0 }}>
												<Box
													sx={{
														position: "relative",
														display: "flex",
														flexDirection: "column",
														gap: "2px",
														marginLeft: "24px",
														padding: "2px 0 4px 12px",
													}}
												>
													<Box
														component="span"
														aria-hidden
														sx={{
															position: "absolute",
															left: 0,
															top: 6,
															bottom: 8,
															width: "1px",
															bgcolor: NAV_TOKENS.divider,
														}}
													/>
													{item.children?.map((child) => {
														const active = child.key === activeKey;
														return (
															<Box
																key={child.key}
																component="button"
																type="button"
																onClick={() => {
																	if (child.onClick) {
																		child.onClick();
																		if (compact) onCloseDrawer();
																	} else if (child.href) go(child.href);
																}}
																tabIndex={childVisible ? 0 : -1}
																aria-current={active ? "page" : undefined}
																sx={{
																	position: "relative",
																	zIndex: 1,
																	display: "flex",
																	alignItems: "center",
																	gap: "8px",
																	width: "100%",
																	height: 36,
																	padding: "0 12px",
																	border: "none",
																	borderRadius: NAV_TOKENS.radiusMd,
																	background: "transparent",
																	cursor: "pointer",
																	fontFamily: NAV_TOKENS.fontSans,
																	fontSize: "13.5px",
																	fontWeight: active ? 500 : 400,
																	color: active ? ACTIVE : NAV_TOKENS.textStrong,
																	boxShadow: active ? SELECTED : "none",
																	whiteSpace: "nowrap",
																	overflow: "hidden",
																	textAlign: "left",
																	transition:
																		"color 150ms ease, background-color 150ms ease, box-shadow 200ms ease",
																	"&:hover": { bgcolor: "rgba(34,3,31,0.04)" },
																}}
															>
																<Box
																	component="span"
																	sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}
																>
																	{child.label}
																</Box>
																{child.dot && <Box component="span" aria-hidden sx={dotSx} />}
															</Box>
														);
													})}
												</Box>
											</Box>
										</Box>
									)}
								</Box>
							);
						})}
					</Box>
				</Box>

				<Box
					sx={{
						flex: "none",
						height: 64,
						display: "flex",
						flexDirection: "column",
						justifyContent: "center",
						padding: "0 24px",
						borderTop: `1px solid ${NAV_TOKENS.borderSubtle}`,
						fontSize: "11.5px",
						lineHeight: 1.5,
						color: NAV_TOKENS.textSecondary,
						whiteSpace: "nowrap",
						overflow: "hidden",
						opacity: labelOpacity,
						transition: "opacity 120ms ease",
					}}
				>
					<span>YouCater Partner · UAE</span>
					<span>© YouCater {new Date().getFullYear()}</span>
				</Box>
			</Box>

			{tip && collapsed && (
				<Box
					role="tooltip"
					sx={{
						position: "fixed",
						left: 80,
						top: tip.top,
						transform: "translateY(-50%)",
						zIndex: 1400,
						zoom,
						pointerEvents: "none",
						padding: "6px 10px",
						borderRadius: "8px",
						bgcolor: NAV_TOKENS.surfaceCard,
						border: `1px solid ${NAV_TOKENS.borderSubtle}`,
						boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
						fontFamily: NAV_TOKENS.fontSans,
						fontSize: "12.5px",
						fontWeight: 500,
						color: NAV_TOKENS.textStrong,
						whiteSpace: "nowrap",
						animation: "ycNavFade 120ms ease",
						"@keyframes ycNavFade": { from: { opacity: 0 }, to: { opacity: 1 } },
					}}
				>
					{tip.label}
				</Box>
			)}
		</>
	);
};

/**
 * The nav entry (item or child key) the current page belongs to. An event detail page opened from a list carries
 * `?type=<list>` (e.g. `/event-detail/:id?type=approved`), which picks that list's entry.
 */
export const sideNavActiveKey = (items: iSideNavItem[], pathname: string, search: string): string | null => {
	const entries = items.flatMap((item) => [item, ...(item.children ?? [])]);
	const exact = entries.find(
		(entry) => entry.href && (pathname === entry.href || pathname.startsWith(`${entry.href}/`)),
	);
	if (exact) return exact.key;
	const type = new URLSearchParams(search).get("type");
	return (type && entries.find((entry) => entry.query === type)?.key) || null;
};

export default SideNav;
